import { Link } from "react-router-dom";
import { BsWhatsapp, BsInstagram, BsLinkedin, BsTelephone, BsEnvelope } from "react-icons/bs";
import { FaFacebookF } from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";

const currentYear = new Date().getFullYear();

const Footer = () => {
  return (
    <footer className="bg-gray-950 font-Montserrat">

      {/* Gold accent bar */}
      <div className="h-1 bg-[#1877F2]" />

      <div className="max-w-7xl mx-auto px-4 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">

          {/* Column 1 — Brand */}
          <div className="lg:col-span-1">
            <Link to="/">
              <img
                src="/images/logo-transparent.png"
                alt="Perfect Shelters"
                className="h-36 w-auto object-contain mb-5"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Nigeria's trusted source for professional architectural plans.
              Quality designs for bungalows, duplexes, flats, and commercial buildings.
            </p>
            {/* Social icons */}
            <div className="flex items-center gap-3">
              {[
                { href: "https://www.facebook.com/macceeengineeringlimited?mibextid=LQQJ4d", icon: <FaFacebookF />, label: "Facebook" },
                { href: "https://wa.me/+2349033020343", icon: <BsWhatsapp />, label: "WhatsApp" },
                { href: "https://instagram.com/perfectsheltersconsult?igshid=NTc4MTIwNjQ2YQ==", icon: <BsInstagram />, label: "Instagram" },
                { href: "https://linkedin.com/in/mcsylvester-nwoye-5042001b2", icon: <BsLinkedin />, label: "LinkedIn" },
              ].map(({ href, icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:border-[#1877F2] hover:text-[#1877F2] transition-colors duration-200"
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 — Quick Links */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-6 flex items-center gap-2">
              <span className="w-4 h-0.5 bg-[#1877F2] rounded-full" />
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { to: "/", label: "Home" },
                { to: "/about", label: "About Us" },
                { to: "/featured-house-plans", label: "All House Plans" },
                { to: "/construction", label: "Construction" },
                { to: "/non-residential", label: "Non-Residential" },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-gray-400 text-sm hover:text-[#1877F2] transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-[#1877F2] transition-all duration-200 rounded-full" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — Plans */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-6 flex items-center gap-2">
              <span className="w-4 h-0.5 bg-[#1877F2] rounded-full" />
              Our Plans
            </h4>
            <ul className="space-y-3">
              {[
                { to: "/bungalows", label: "Bungalows" },
                { to: "/duplex", label: "Duplex" },
                { to: "/flat", label: "Flats & Apartments" },
                { to: "/non-residential", label: "Commercial Plans" },
                { to: "/featured-house-plans", label: "View All Plans" },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-gray-400 text-sm hover:text-[#1877F2] transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-[#1877F2] transition-all duration-200 rounded-full" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — Contact */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-6 flex items-center gap-2">
              <span className="w-4 h-0.5 bg-[#1877F2] rounded-full" />
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <BsTelephone className="text-[#1877F2] mt-0.5 flex-shrink-0" />
                <a
                  href="tel:+2349033020343"
                  className="text-gray-400 text-sm hover:text-[#1877F2] transition-colors duration-200"
                >
                  +234 903 302 0343
                </a>
              </li>
              <li className="flex items-start gap-3">
                <BsEnvelope className="text-[#1877F2] mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:info@perfectshelters.com"
                  className="text-gray-400 text-sm hover:text-[#1877F2] transition-colors duration-200 break-all"
                >
                  info@perfectshelters.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MdLocationOn className="text-[#1877F2] mt-0.5 flex-shrink-0 text-base" />
                <span className="text-gray-400 text-sm leading-relaxed">
                  Nigeria
                </span>
              </li>
              <li className="pt-2">
                <a
                  href="https://wa.me/+2349033020343?text=Hello, I'm interested in your house plans."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors duration-200"
                >
                  <BsWhatsapp />
                  Chat on WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="text-gray-500 text-xs text-center md:text-left">
            &copy; {currentYear} Perfect Shelters Consults. All rights reserved.
          </p>
          <p className="text-gray-600 text-xs">
            Designed by{" "}
            <a
              href="https://klincode.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1877F2] hover:underline"
            >
              Klincode
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
